import React, { useState } from 'react';
import { Calendar, SlidersHorizontal } from 'lucide-react';
import FuelOverview from './FuelOverview';
import FuelEntries from './FuelEntries';
import FuelReports from './FuelReports';
import FuelEntryForm from './FuelEntryForm';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'entries', label: 'Entries' },
  { id: 'reports', label: 'Reports' },
];

const TIME_OPTIONS = [
  { label: 'This Month', value: 'thisMonth' },
  { label: 'Last Month', value: 'lastMonth' },
  { label: 'Last 3 Months', value: 'last3Months' },
  { label: 'This Year', value: 'thisYear' },
  { label: 'Custom Range', value: 'custom' },
];

const FuelManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('thisMonth');
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [editData, setEditData] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = () => setRefreshKey((k) => k + 1);

  const handleEdit = (entry) => {
    setEditData(entry);
    setShowAddEntry(true);
  };

  const handleAddEntry = () => {
    setEditData(null);
    setShowAddEntry(true);
  };

  const selectedLabel = TIME_OPTIONS.find((o) => o.value === timeRange)?.label;

  return (
    <div className="min-h-screen bg-[#f8f9fb]">
      <div className="max-w-7xl mx-auto space-y-5">

        {/* ── Page Header ── */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Fuel Management Overview</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Track fuel expenses, mileage and vehicle efficiency.
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* Time Selector */}
            <div className="relative">
              <button
                onClick={() => setShowTimePicker((v) => !v)}
                className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white hover:bg-gray-50 text-gray-700 font-medium shadow-sm"
              >
                <Calendar className="h-4 w-4 text-gray-400" />
                {selectedLabel}
              </button>
              {showTimePicker && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowTimePicker(false)}
                  />
                  <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden">
                    {TIME_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => {
                          setTimeRange(opt.value);
                          setShowTimePicker(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${
                          timeRange === opt.value
                            ? 'text-blue-600 font-semibold bg-blue-50'
                            : 'text-gray-700'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Filters */}
            <button className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white hover:bg-gray-50 text-gray-700 font-medium shadow-sm">
              <SlidersHorizontal className="h-4 w-4 text-gray-400" />
              Filters
            </button>
          </div>
        </div>

        {/* ── Tab Navigation ── */}
        <div className="flex gap-0 border-b border-gray-200">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 pb-3 pt-1 text-sm font-medium border-b-2 transition-colors mr-2 ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Tab Content ── */}
        {activeTab === 'overview' && (
          <FuelOverview
            key={`overview-${refreshKey}`}
            timeRange={timeRange}
            onViewEntries={() => setActiveTab('entries')}
          />
        )}
        {activeTab === 'entries' && (
          <FuelEntries
            key={`entries-${refreshKey}`}
            onEdit={handleEdit}
            onAddEntry={handleAddEntry}
            onRefresh={handleRefresh}
          />
        )}
        {activeTab === 'reports' && (
          <FuelReports key={`reports-${refreshKey}`} />
        )}
      </div>

      {/* ── Add / Edit Entry Modal ── */}
      <Dialog
        open={showAddEntry}
        onOpenChange={(open) => {
          setShowAddEntry(open);
          if (!open) setEditData(null);
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editData ? 'Edit Fuel Entry' : 'Add Missed Entry'}
            </DialogTitle>
          </DialogHeader>
          <FuelEntryForm
            editData={editData}
            onSuccess={() => {
              setShowAddEntry(false);
              setEditData(null);
              handleRefresh();
            }}
            onCancel={() => {
              setShowAddEntry(false);
              setEditData(null);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FuelManagement;
